"use client";

import { useEffect, useRef } from "react";
import { Globe, Search, MapPin, Briefcase, ShoppingCart, Settings, FileText, Bot } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const rituals = [{
  icon: Globe,
  number: "01",
  title: "National SEO",
  description: "I build authority across the UK, targeting high-intent keywords to capture market share and drive consistent, organic growth for your business.",
  meta: "Tailored Strategy",
  link: "/NationalSeo"
}, {
  icon: Search,
  number: "02",
  title: "International SEO",
  description: "I scale your reach globally, managing hreflang, cultural nuances, and technical architecture to improve visibility across target international markets.",
  meta: "Global Reach",
  link: "/InternationalSeo"
}, {
  icon: MapPin,
  number: "03",
  title: "Local SEO",
  description: "I compete for high-intent local searches with Google Business Profile optimisation and local citation work that brings nearby customers directly to your door.",
  meta: "Local Visibility",
  link: "/LocalSEO"
}, {
  icon: Briefcase,
  number: "04",
  title: "B2B SEO & Leads",
  description: "I focus on high-value B2B search intent, creating content and technical structures that convert professional researchers into qualified sales leads.",
  meta: "Lead Focused",
  link: "/B2BSeoLeadGeneration"
}, {
  icon: ShoppingCart,
  number: "05",
  title: "Ecommerce SEO",
  description: "I optimise product architecture and category structures to increase visibility, improve user flow, and boost conversion rates for online stores.",
  meta: "Revenue Growth",
  link: "/EcommerceSeo"
}, {
  icon: Settings,
  number: "06",
  title: "Technical SEO",
  description: "I resolve complex crawl issues, site speed bottlenecks, and indexing errors to help search engines crawl and understand your website.",
  meta: "Site Health",
  link: "/TechnicalSeo"
}, {
  icon: FileText,
  number: "07",
  title: "SEO Audits",
  description: "I provide deep-dive, actionable audits that identify hidden opportunities and critical fixes to unlock your site's full organic potential.",
  meta: "Actionable Data",
  link: "/SeoAudits"
}, {
  icon: Bot,
  number: "08",
  title: "AI Search Optimisation",
  description: "I prepare your brand for the future of search, optimising for AI-powered answers and modern search experiences across all platforms.",
  meta: "Future Ready",
  link: "/AISearchOptimisation"
}];
export default function Services() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const cardsRef = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (!sectionRef.current) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();
      mm.add({
        reduced: "(prefers-reduced-motion: reduce)",
        normal: "(prefers-reduced-motion: no-preference)"
      }, context => {
        const conditions = context.conditions as {
          reduced: boolean;
          normal: boolean;
        };
        const validCards = cardsRef.current.filter((el): el is HTMLDivElement => el !== null);
        if (conditions.reduced) {
          gsap.set(validCards, {
            opacity: 1,
            y: 0
          });
          return;
        }
        gsap.from(validCards, {
          y: 32,
          duration: 0.72,
          stagger: 0.12,
          ease: "power3.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 75%",
            once: true
          }
        });
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);
  return <section data-section-id="23037" ref={sectionRef} id="my-seo-consultancy-services" className="bg-background text-foreground py-32 md:py-48 px-6 md:px-12 lg:px-20">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-8 mb-24 md:mb-32">
          <div className="md:col-span-2">
            <span className="font-sans uppercase font-semibold text-muted-foreground" style={{
            fontSize: "0.9375rem",
            letterSpacing: "0.08em"
          }}>
              MY SERVICES
            </span>
          </div>
          <div className="md:col-span-10">
            <h2 className="font-serif text-foreground" style={{
            fontWeight: 700,
            fontSize: "clamp(1.75rem, 2.75vw, 2.375rem)",
            letterSpacing: "-0.03em",
            lineHeight: 1.1
          }}>
              {"Tailored SEO strategies for "}
              <span className="relative inline-block italic" style={{
              fontWeight: 600
            }}>
                sustainable growth
                <svg className="absolute left-0 -bottom-2 w-full" viewBox="0 0 200 14" preserveAspectRatio="none" aria-hidden="true" style={{
                height: "0.35em"
              }}>
                  <path d="M2 8 Q 50 2, 100 6 T 198 5" stroke="hsl(340, 75%, 65%)" strokeWidth="4" strokeLinecap="round" fill="none" />
                </svg>
              </span>
              {"."}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-8">
          {rituals.map((ritual, i) => {
          const Icon = ritual.icon;
          const linkHref = typeof ritual.link === "string" ? ritual.link : ritual.link.href;
          const isNewTab = typeof ritual.link === "object" ? ritual.link.newTab : false;
          return <div key={i} data-index={i} ref={el => {
            cardsRef.current[i] = el;
          }} className="group bg-card border border-border rounded-lg p-8 md:p-10 lg:p-12 flex flex-col min-h-[380px] transition-all duration-300 hover:border-primary hover:shadow-lg">
                <div className="mb-8">
                  <Icon className="transition-transform duration-300 group-hover:translate-x-1" style={{
                width: 32,
                height: 32,
                strokeWidth: 1.5,
                color: "hsl(220, 80%, 30%)"
              }} aria-hidden="true" />
                </div>

                <div className="font-sans uppercase font-semibold text-muted-foreground mb-4" style={{
              fontSize: "16px",
              letterSpacing: "0.08em"
            }}>
                  SERVICE - {ritual.number}
                </div>

                <h3 className="font-serif text-foreground mb-5" style={{
              fontWeight: 600,
              fontSize: "clamp(1.375rem, 1.8vw, 1.625rem)",
              letterSpacing: "-0.02em",
              lineHeight: 1.2
            }}>
                  {ritual.title}
                </h3>

                <p className="font-sans text-foreground/80 mb-10 flex-grow" style={{
              fontSize: "1.125rem",
              lineHeight: 1.6
            }}>
                  {ritual.description}
                </p>

                <div className="flex items-center justify-between gap-4 mt-auto">
                  <span className="font-sans text-muted-foreground" style={{
                fontSize: "1rem",
                letterSpacing: "0em"
              }}>
                    {ritual.meta}
                  </span>
                  <Button asChild className="rounded-md bg-primary text-primary-foreground hover:bg-primary/90 px-7 py-3 h-auto font-sans font-semibold transition-all duration-300 text-base">
                    <Link to={linkHref} newTab={isNewTab}>
                      Learn More
                    </Link>
                  </Button>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}